/**
 * Service unifié de gestion clients/prospects pour OMAD SERVICES
 * 
 * Gère la transition transparente prospect → client avec conservation de l'historique
 */

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  addDoc,
  Timestamp 
} from 'firebase/firestore';

// Types d'entités unifiés
export const ENTITY_TYPES = {
  PROSPECT: 'prospect',
  CLIENT: 'client'
};

// Statuts commerciaux (pipeline de vente)
export const COMMERCIAL_STATUS = {
  CONTACT: 'contact',
  DEVIS_ENVOYE: 'devis_envoye',
  NEGOCIATION: 'negociation',
  SIGNE: 'signe',
  PERDU: 'perdu'
};

  // Statuts bénéficiaires (après conversion)
export const CLIENT_STATUS = {
  ACTIF: 'actif',
  INACTIF: 'inactif',
  SUSPENDU: 'suspendu'
};

// Labels d'affichage
export const STATUS_LABELS = {
  // Statuts commerciaux
  [COMMERCIAL_STATUS.CONTACT]: 'Contact',
  [COMMERCIAL_STATUS.DEVIS_ENVOYE]: 'Devis envoyé',
  [COMMERCIAL_STATUS.NEGOCIATION]: 'Négociation',
  [COMMERCIAL_STATUS.SIGNE]: 'Signé',
  [COMMERCIAL_STATUS.PERDU]: 'Perdu',
  
  // Statuts bénéficiaires
  [CLIENT_STATUS.ACTIF]: 'Bénéficiaire actif',
  [CLIENT_STATUS.INACTIF]: 'Bénéficiaire inactif',
  [CLIENT_STATUS.SUSPENDU]: 'Bénéficiaire suspendu'
};

// Couleurs associées
export const STATUS_COLORS = {
  // Statuts commerciaux
  [COMMERCIAL_STATUS.CONTACT]: 'bg-blue-100 text-blue-800',
  [COMMERCIAL_STATUS.DEVIS_ENVOYE]: 'bg-orange-100 text-orange-800',
  [COMMERCIAL_STATUS.NEGOCIATION]: 'bg-yellow-100 text-yellow-800',
  [COMMERCIAL_STATUS.SIGNE]: 'bg-green-100 text-green-800',
  [COMMERCIAL_STATUS.PERDU]: 'bg-red-100 text-red-800',
  
  // Statuts bénéficiaires
  [CLIENT_STATUS.ACTIF]: 'bg-green-100 text-green-800',
  [CLIENT_STATUS.INACTIF]: 'bg-gray-100 text-gray-800',
  [CLIENT_STATUS.SUSPENDU]: 'bg-yellow-100 text-yellow-800'
};

/**
 * Crée une entité unifiée (prospect ou client) compatible avec la structure existante
 */
export const creerEntiteUnifiee = async (db, donnees) => {
  try {
    // Adapter les données pour la compatibilité avec la structure existante
    const entiteData = {
      // Données de base (structure existante)
      firstName: donnees.prenom || donnees.firstName || '',
      lastName: donnees.nom || donnees.lastName || '',
      email: donnees.email || '',
      phoneNumber: donnees.telephone || donnees.phoneNumber || '',
      address: donnees.adresse || donnees.address || '',
      addressNumber: donnees.addressNumber || '',
      color: donnees.color || '#4FC3F7',
      isActive: donnees.typeEntite === ENTITY_TYPES.CLIENT ? true : false,
      
      // Métadonnées système (nouveaux champs)
      typeEntite: donnees.typeEntite || ENTITY_TYPES.PROSPECT,
      dateCreation: Timestamp.now(),
      dateModification: Timestamp.now(),
      
      // Statuts par défaut
      statutCommercial: donnees.statutCommercial || COMMERCIAL_STATUS.CONTACT,
      statutClient: donnees.statutClient || null,
      
      // Historique initial
      historique: [
        {
          date: Timestamp.now(),
          evenement: 'creation',
          details: `Création en tant que ${donnees.typeEntite || 'prospect'}`,
          utilisateur: donnees.createur || 'system'
        }
      ]
    };

    const docRef = await addDoc(collection(db, 'clients'), entiteData);
    console.log(`✅ Entité créée: ${docRef.id} (${entiteData.typeEntite})`);
    
    return { id: docRef.id, ...entiteData };
    
  } catch (error) {
    console.error('Erreur création entité unifiée:', error);
    throw error;
  }
};

/**
 * Convertit un prospect en client en maintenant la compatibilité avec la structure existante
 */
export const convertirProspectEnClient = async (db, prospectId, devisId, options = {}) => {
  try {
    console.log(`🔄 Conversion prospect → client: ${prospectId}`);
    
    const prospectRef = doc(db, 'clients', prospectId);
    const maintenant = Timestamp.now();
    
    const updateData = {
      typeEntite: ENTITY_TYPES.CLIENT,
      statutClient: CLIENT_STATUS.ACTIF,
      statutCommercial: COMMERCIAL_STATUS.SIGNE,
      dateConversion: maintenant,
      dateModification: maintenant,
      // Maintenir la compatibilité avec l'ancien système
      isActive: true
    };

    // Ajouter à l'historique
    const evenementConversion = {
      date: maintenant,
      evenement: 'conversion_client',
      details: `Conversion en client suite à l'acceptation du devis ${devisId}`,
      devisId: devisId,
      utilisateur: options.utilisateur || 'system'
    };

    // Récupérer l'historique existant
    const prospectDoc = await getDocs(query(collection(db, 'clients'), where('__name__', '==', prospectId)));
    const prospectData = prospectDoc.docs[0]?.data();
    const historiqueExistant = prospectData?.historique || [];

    await updateDoc(prospectRef, {
      ...updateData,
      historique: [...historiqueExistant, evenementConversion]
    });

    console.log(`✅ Prospect ${prospectId} converti en client actif`);
    
    return {
      success: true,
      entiteId: prospectId,
      typeAncien: ENTITY_TYPES.PROSPECT,
      typeNouveau: ENTITY_TYPES.CLIENT,
      devisId: devisId
    };
    
  } catch (error) {
    console.error('Erreur conversion prospect → client:', error);
    throw error;
  }
};

/**
 * Récupère toutes les entités avec filtrage
 */
export const obtenirEntites = async (db, filtres = {}) => {
  try {
    let entitesQuery = query(collection(db, 'clients'), orderBy('dateCreation', 'desc'));

    // Appliquer les filtres de manière conditionnelle
    if (filtres.typeEntite && filtres.typeEntite !== 'all') {
      entitesQuery = query(entitesQuery, where('typeEntite', '==', filtres.typeEntite));
    }

    // Pour les statuts commerciaux, ne filtrer que si c'est un prospect
    if (filtres.statutCommercial && filtres.statutCommercial !== 'all') {
      // Si on filtre par statut commercial, on s'assure que c'est un prospect
      entitesQuery = query(entitesQuery, 
        where('typeEntite', '==', ENTITY_TYPES.PROSPECT),
        where('statutCommercial', '==', filtres.statutCommercial)
      );
    }

    // Pour les statuts clients, ne filtrer que si c'est un client
    if (filtres.statutClient && filtres.statutClient !== 'all') {
      // Si on filtre par statut client, on s'assure que c'est un client
      entitesQuery = query(entitesQuery, 
        where('typeEntite', '==', ENTITY_TYPES.CLIENT),
        where('statutClient', '==', filtres.statutClient)
      );
    }

    const snapshot = await getDocs(entitesQuery);
    const entites = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convertir les timestamps
      dateCreation: doc.data().dateCreation?.toDate(),
      dateModification: doc.data().dateModification?.toDate(),
      dateConversion: doc.data().dateConversion?.toDate()
    }));

    // Filtrer par recherche si nécessaire (compatible avec la structure existante)
    if (filtres.recherche) {
      const terme = filtres.recherche.toLowerCase();
      return entites.filter(entite => 
        entite.lastName?.toLowerCase().includes(terme) ||
        entite.firstName?.toLowerCase().includes(terme) ||
        entite.email?.toLowerCase().includes(terme) ||
        entite.nom?.toLowerCase().includes(terme) || // Compatibilité ascendante
        entite.prenom?.toLowerCase().includes(terme) // Compatibilité ascendante
      );
    }

    return entites;

  } catch (error) {
    console.error('Erreur récupération entités:', error);
    throw error;
  }
};

/**
 * Met à jour le statut d'une entité (commercial pour prospects, client pour clients)
 */
export const mettreAJourStatutEntite = async (db, entiteId, nouveauStatut, options = {}) => {
  try {
    const entiteRef = doc(db, 'clients', entiteId);
    const maintenant = Timestamp.now();

    // Déterminer si c'est un statut commercial ou client
    const estStatutCommercial = Object.values(COMMERCIAL_STATUS).includes(nouveauStatut);
    const estStatutClient = Object.values(CLIENT_STATUS).includes(nouveauStatut);

    if (!estStatutCommercial && !estStatutClient) {
      throw new Error('Statut non reconnu');
    }

    // Créer l'objet de mise à jour en évitant les valeurs undefined
    const updateData = {
      dateModification: maintenant
    };

    if (estStatutCommercial) {
      updateData.statutCommercial = nouveauStatut;
    } else if (estStatutClient) {
      updateData.statutClient = nouveauStatut;
    }

    // Ajouter à l'historique
    const evenementStatut = {
      date: maintenant,
      evenement: estStatutCommercial ? 'changement_statut_commercial' : 'changement_statut_client',
      details: `Statut ${estStatutCommercial ? 'commercial' : 'client'} changé vers: ${STATUS_LABELS[nouveauStatut]}`,
      nouveauStatut: nouveauStatut,
      utilisateur: options.utilisateur || 'system'
    };

    // Ajouter ancienStatut seulement s'il est défini
    if (options.ancienStatut !== undefined) {
      evenementStatut.ancienStatut = options.ancienStatut;
    }

    // Récupérer l'entité pour obtenir l'historique existant
    const entiteDoc = await getDocs(query(collection(db, 'clients'), where('__name__', '==', entiteId)));
    const entiteData = entiteDoc.docs[0]?.data();
    const historiqueExistant = entiteData?.historique || [];

    // Filtrer les valeurs undefined de l'objet de mise à jour
    const updateDataFiltre = Object.fromEntries(
      Object.entries(updateData).filter(([_, value]) => value !== undefined)
    );

    await updateDoc(entiteRef, {
      ...updateDataFiltre,
      historique: [...historiqueExistant, evenementStatut]
    });

    console.log(`✅ Statut mis à jour: ${entiteId} → ${nouveauStatut}`);
    
  } catch (error) {
    console.error('Erreur mise à jour statut:', error);
    throw error;
  }
};

/**
 * Obtient les statistiques du pipeline
 */
export const obtenirStatistiquesPipeline = async (db) => {
  try {
    const entites = await obtenirEntites(db);
    
    const stats = {
      total: entites.length,
      
      // Par type d'entité
      prospects: entites.filter(e => e.typeEntite === ENTITY_TYPES.PROSPECT).length,
      clients: entites.filter(e => e.typeEntite === ENTITY_TYPES.CLIENT).length,
      
      // Par statut commercial (prospects)
      contact: entites.filter(e => e.statutCommercial === COMMERCIAL_STATUS.CONTACT).length,
      devisEnvoye: entites.filter(e => e.statutCommercial === COMMERCIAL_STATUS.DEVIS_ENVOYE).length,
      negociation: entites.filter(e => e.statutCommercial === COMMERCIAL_STATUS.NEGOCIATION).length,
      signe: entites.filter(e => e.statutCommercial === COMMERCIAL_STATUS.SIGNE).length,
      perdu: entites.filter(e => e.statutCommercial === COMMERCIAL_STATUS.PERDU).length,
      
      // Par statut client
      clientsActifs: entites.filter(e => e.statutClient === CLIENT_STATUS.ACTIF).length,
      clientsInactifs: entites.filter(e => e.statutClient === CLIENT_STATUS.INACTIF).length,
      clientsSuspendus: entites.filter(e => e.statutClient === CLIENT_STATUS.SUSPENDU).length,
      
      // Taux de conversion
      tauxConversion: entites.length > 0 ? 
        Math.round((entites.filter(e => e.typeEntite === ENTITY_TYPES.CLIENT).length / entites.length) * 100) : 0
    };

    return stats;

  } catch (error) {
    console.error('Erreur calcul statistiques pipeline:', error);
    throw error;
  }
};

/**
 * Initialise les données pour un nouveau système (pas de migration nécessaire)
 */
export const initialiserSystemeUnifie = async (db) => {
  try {
    console.log('✅ Système unifié prêt à être utilisé');
    console.log('💡 Aucune migration nécessaire - système compatible avec la structure existante');
    
    return {
      success: true,
      message: 'Système unifié initialisé avec succès',
      clientsExistants: 0, // À déterminer lors de l'utilisation
      prospectsExistants: 0 // Pas de prospects à migrer
    };

  } catch (error) {
    console.error('Erreur initialisation système unifié:', error);
    throw error;
  }
};

export default {
  ENTITY_TYPES,
  COMMERCIAL_STATUS,
  CLIENT_STATUS,
  STATUS_LABELS,
  STATUS_COLORS,
  creerEntiteUnifiee,
  convertirProspectEnClient,
  obtenirEntites,
  mettreAJourStatutEntite,
  obtenirStatistiquesPipeline,
  initialiserSystemeUnifie
};
